import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, TextInput, StyleSheet, ActivityIndicator, ScrollView, Modal, TouchableWithoutFeedback } from 'react-native';
import { X, Plus, Search, Check, Building, MapPin, Phone, Send, Globe, MessageCircle, ChevronDown, XCircle, CheckCircle2, AlertCircle } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { shadows } from '../lib/styles';

type ContactMethod = 'text' | 'phone' | 'email' | 'web_form';

interface Provider {
  id: string;
  name: string;
  specialty: string;
  reason?: string;
  skipped?: boolean;
  suggested_contact_method?: ContactMethod;
  contact_method?: ContactMethod;
  contact_method_reason?: string;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  can_contact_via_call?: 'yes' | 'no' | 'unknown' | null;
  can_contact_via_text?: 'yes' | 'no' | 'unknown' | null;
  can_contact_via_email?: 'yes' | 'no' | 'unknown' | null;
}

interface ServiceProvider {
  id: string;
  company_name: string;
  primary_category: string | null;
  city: string | null;
  state: string | null;
  rating: number | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  can_contact_via_call: 'yes' | 'no' | 'unknown' | null;
  can_contact_via_text: 'yes' | 'no' | 'unknown' | null;
  can_contact_via_email: 'yes' | 'no' | 'unknown' | null;
}

interface ProviderSelectionListProps {
  initialProviders: Provider[];
  onProvidersChange: (providers: Provider[]) => void;
  disabled?: boolean;
}

const CONTACT_METHOD_CONFIG: Record<ContactMethod, { label: string; icon: any; color: string }> = {
  text: { label: 'Text/SMS', icon: MessageCircle, color: '#8B5CF6' },
  phone: { label: 'Phone Call', icon: Phone, color: '#2563EB' },
  email: { label: 'Email', icon: Send, color: '#16A34A' },
  web_form: { label: 'Web Form', icon: Globe, color: '#F59E0B' },
};

export const ProviderSelectionList: React.FC<ProviderSelectionListProps> = ({
  initialProviders,
  onProvidersChange,
  disabled = false,
}) => {
  const [providers, setProviders] = useState<Provider[]>(initialProviders);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ServiceProvider[]>([]);
  const [searching, setSearching] = useState(false);
  const [showTypeahead, setShowTypeahead] = useState(false);
  const [contactMethodModalVisible, setContactMethodModalVisible] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);

  useEffect(() => {
    setProviders(initialProviders);
  }, [initialProviders]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      searchProviders(searchQuery);
    } else {
      setSearchResults([]);
      setShowTypeahead(false);
    }
  }, [searchQuery]);

  const searchProviders = async (query: string) => {
    setSearching(true);
    try {
      const { data, error } = await supabase
        .from('service_providers')
        .select('id, company_name, primary_category, city, state, rating, phone, email, website, can_contact_via_call, can_contact_via_text, can_contact_via_email')
        .or(`company_name.ilike.%${query}%,primary_category.ilike.%${query}%`)
        .eq('status', 'active')
        .order('rating', { ascending: false, nullsLast: true })
        .limit(10);

      if (error) throw error;
      
      setSearchResults(data || []);
      setShowTypeahead(true);
    } catch (err) {
      console.error('Error searching providers:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const selectBestContactMethod = (provider: ServiceProvider): { method: ContactMethod, reason: string } => {
    // Check text first (most preferred)
    if (provider.phone && provider.can_contact_via_text === 'yes') {
      return { method: 'text', reason: 'Provider accepts SMS, phone number available' };
    }
    
    // Check call second
    if (provider.phone && provider.can_contact_via_call === 'yes') {
      return { method: 'phone', reason: 'Provider accepts calls, phone number available' };
    }
    
    // Check email third
    if (provider.email && provider.can_contact_via_email === 'yes') {
      return { method: 'email', reason: 'Provider accepts email, email address available' };
    }
    
    // Handle 'unknown' preference cases - still suggest if we have the contact info
    if (provider.phone && (provider.can_contact_via_text === 'unknown' || provider.can_contact_via_text === null)) {
      return { method: 'text', reason: 'Phone number available (preference unknown)' };
    }
    
    if (provider.phone && (provider.can_contact_via_call === 'unknown' || provider.can_contact_via_call === null)) {
      return { method: 'phone', reason: 'Phone number available (preference unknown)' };
    }
    
    if (provider.email && (provider.can_contact_via_email === 'unknown' || provider.can_contact_via_email === null)) {
      return { method: 'email', reason: 'Email available (preference unknown)' };
    }
    
    // Fallback to web_form
    return { method: 'web_form', reason: 'Using web form as fallback' };
  };

  const handleSkipProvider = (providerId: string) => {
    const updatedProviders = providers.map(p =>
      p.id === providerId ? { ...p, skipped: !p.skipped } : p
    );
    setProviders(updatedProviders);
    onProvidersChange(updatedProviders);
  };

  const handleAddProvider = (serviceProvider: ServiceProvider) => {
    const contactMethodSelection = selectBestContactMethod(serviceProvider);
    
    const newProvider: Provider = {
      id: serviceProvider.id,
      name: serviceProvider.company_name,
      specialty: serviceProvider.primary_category || 'General Services',
      reason: 'Manually added by human',
      skipped: false,
      suggested_contact_method: contactMethodSelection.method,
      contact_method: contactMethodSelection.method,
      contact_method_reason: contactMethodSelection.reason,
      phone: serviceProvider.phone,
      email: serviceProvider.email,
      website: serviceProvider.website,
      can_contact_via_call: serviceProvider.can_contact_via_call,
      can_contact_via_text: serviceProvider.can_contact_via_text,
      can_contact_via_email: serviceProvider.can_contact_via_email,
    };

    // Check if provider already exists
    if (providers.find(p => p.id === serviceProvider.id)) {
      return; // Already in list
    }

    const updatedProviders = [...providers, newProvider];
    setProviders(updatedProviders);
    onProvidersChange(updatedProviders);
    
    // Clear search
    setSearchQuery('');
    setShowTypeahead(false);
  };

  const handleContactMethodChange = (providerId: string, method: ContactMethod) => {
    const updatedProviders = providers.map(p => {
      if (p.id === providerId) {
        return {
          ...p,
          contact_method: method,
          // Keep suggested_contact_method unchanged to show the override
        };
      }
      return p;
    });
    setProviders(updatedProviders);
    onProvidersChange(updatedProviders);
    setContactMethodModalVisible(false);
    setSelectedProviderId(null);
  };

  const openContactMethodModal = (providerId: string) => {
    if (disabled) return;
    setSelectedProviderId(providerId);
    setContactMethodModalVisible(true);
  };

  const getContactMethodAvailability = (provider: Provider, method: ContactMethod): { available: boolean; reason: string; icon: any; color: string } => {
    switch (method) {
      case 'text':
        if (!provider.phone) {
          return { available: false, reason: 'No phone number', icon: XCircle, color: '#DC2626' };
        }
        if (provider.can_contact_via_text === 'no') {
          return { available: false, reason: 'Provider does not accept SMS', icon: XCircle, color: '#DC2626' };
        }
        if (provider.can_contact_via_text === 'yes') {
          return { available: true, reason: 'Provider accepts SMS', icon: CheckCircle2, color: '#16A34A' };
        }
        return { available: true, reason: 'Preference unknown', icon: AlertCircle, color: '#F59E0B' };
      
      case 'phone':
        if (!provider.phone) {
          return { available: false, reason: 'No phone number', icon: XCircle, color: '#DC2626' };
        }
        if (provider.can_contact_via_call === 'no') {
          return { available: false, reason: 'Provider does not accept calls', icon: XCircle, color: '#DC2626' };
        }
        if (provider.can_contact_via_call === 'yes') {
          return { available: true, reason: 'Provider accepts calls', icon: CheckCircle2, color: '#16A34A' };
        }
        return { available: true, reason: 'Preference unknown', icon: AlertCircle, color: '#F59E0B' };
      
      case 'email':
        if (!provider.email) {
          return { available: false, reason: 'No email address', icon: XCircle, color: '#DC2626' };
        }
        if (provider.can_contact_via_email === 'no') {
          return { available: false, reason: 'Provider does not accept email', icon: XCircle, color: '#DC2626' };
        }
        if (provider.can_contact_via_email === 'yes') {
          return { available: true, reason: 'Provider accepts email', icon: CheckCircle2, color: '#16A34A' };
        }
        return { available: true, reason: 'Preference unknown', icon: AlertCircle, color: '#F59E0B' };
      
      case 'web_form':
        return { available: true, reason: 'Fallback method', icon: CheckCircle2, color: '#16A34A' };
      
      default:
        return { available: false, reason: 'Unknown method', icon: XCircle, color: '#DC2626' };
    }
  };

  const handleRemoveProvider = (providerId: string) => {
    const updatedProviders = providers.filter(p => p.id !== providerId);
    setProviders(updatedProviders);
    onProvidersChange(updatedProviders);
  };

  const activeProviders = providers.filter(p => !p.skipped);
  const skippedProviders = providers.filter(p => p.skipped);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Building size={16} color="#2563EB" />
        <Text style={styles.headerText}>
          Service Providers to Contact ({activeProviders.length})
        </Text>
      </View>

      {/* Active Providers */}
      <View style={styles.providersList}>
        {activeProviders.map((provider, index) => {
          const contactMethod = provider.contact_method || provider.suggested_contact_method;
          const methodConfig = contactMethod ? CONTACT_METHOD_CONFIG[contactMethod] : null;
          const MethodIcon = methodConfig?.icon;
          
          return (
            <View key={provider.id} style={styles.providerCard}>
              <View style={styles.providerNumber}>
                <Text style={styles.providerNumberText}>{index + 1}</Text>
              </View>
              <View style={styles.providerContent}>
                <Text style={styles.providerName}>{provider.name}</Text>
                <Text style={styles.providerSpecialty}>{provider.specialty}</Text>
                {provider.reason && (
                  <Text style={styles.providerReason}>{provider.reason}</Text>
                )}
                
                {/* Contact Method Display */}
                {contactMethod && methodConfig && (
                  <View style={styles.contactMethodContainer}>
                    <View style={styles.contactMethodRow}>
                      <Text style={styles.contactMethodLabel}>Contact via:</Text>
                      <Pressable
                        style={[styles.contactMethodBadge, { backgroundColor: `${methodConfig.color}15` }]}
                        onPress={() => openContactMethodModal(provider.id)}
                        disabled={disabled}
                      >
                        <MethodIcon size={12} color={methodConfig.color} />
                        <Text style={[styles.contactMethodText, { color: methodConfig.color }]}>
                          {methodConfig.label}
                        </Text>
                        {!disabled && <ChevronDown size={12} color={methodConfig.color} />}
                      </Pressable>
                    </View>
                    {provider.suggested_contact_method && provider.contact_method !== provider.suggested_contact_method && (
                      <Text style={styles.contactMethodOverride}>
                        (AI suggested: {CONTACT_METHOD_CONFIG[provider.suggested_contact_method].label})
                      </Text>
                    )}
                  </View>
                )}
              </View>
              <View style={styles.providerActions}>
                <Pressable
                  style={styles.actionButton}
                  onPress={() => handleSkipProvider(provider.id)}
                  disabled={disabled}
                >
                  <X size={16} color="#DC2626" />
                  <Text style={styles.skipButtonText}>Skip</Text>
                </Pressable>
              </View>
            </View>
          );
        })}
      </View>

      {/* Skipped Providers */}
      {skippedProviders.length > 0 && (
        <View style={styles.skippedSection}>
          <Text style={styles.skippedTitle}>Skipped ({skippedProviders.length})</Text>
          {skippedProviders.map((provider) => (
            <View key={provider.id} style={styles.skippedCard}>
              <View style={styles.providerContent}>
                <Text style={styles.skippedName}>{provider.name}</Text>
                <Text style={styles.skippedSpecialty}>{provider.specialty}</Text>
              </View>
              <Pressable
                style={styles.actionButton}
                onPress={() => handleSkipProvider(provider.id)}
                disabled={disabled}
              >
                <Check size={16} color="#16A34A" />
                <Text style={styles.restoreButtonText}>Restore</Text>
              </Pressable>
            </View>
          ))}
        </View>
      )}

      {/* Add Provider Search */}
      {!disabled && (
        <View style={styles.addSection}>
          <Text style={styles.addTitle}>Add Another Provider</Text>
          <View style={styles.searchContainer}>
            <View style={styles.searchInputContainer}>
              <Search size={16} color="#9CA3AF" />
              <TextInput
                style={styles.searchInput}
                placeholder="Search by name or specialty..."
                value={searchQuery}
                onChangeText={setSearchQuery}
                editable={!disabled}
              />
              {searching && <ActivityIndicator size="small" color="#9CA3AF" />}
            </View>

            {/* Typeahead Results */}
            {showTypeahead && searchResults.length > 0 && (
              <View style={styles.typeaheadDropdown}>
                <ScrollView style={styles.typeaheadScroll}>
                  {searchResults.map((result) => {
                    const alreadyAdded = providers.find(p => p.id === result.id);
                    return (
                      <Pressable
                        key={result.id}
                        style={[
                          styles.typeaheadItem,
                          alreadyAdded && styles.typeaheadItemDisabled
                        ]}
                        onPress={() => !alreadyAdded && handleAddProvider(result)}
                        disabled={!!alreadyAdded}
                      >
                        <View style={styles.typeaheadItemContent}>
                          <View style={styles.typeaheadItemHeader}>
                            <Text style={styles.typeaheadItemName}>
                              {result.company_name}
                            </Text>
                            {alreadyAdded && (
                              <View style={styles.addedBadge}>
                                <Text style={styles.addedBadgeText}>Added</Text>
                              </View>
                            )}
                          </View>
                          {result.primary_category && (
                            <Text style={styles.typeaheadItemCategory}>
                              {result.primary_category}
                            </Text>
                          )}
                          {(result.city || result.state) && (
                            <View style={styles.typeaheadItemLocation}>
                              <MapPin size={12} color="#9CA3AF" />
                              <Text style={styles.typeaheadItemLocationText}>
                                {[result.city, result.state].filter(Boolean).join(', ')}
                              </Text>
                            </View>
                          )}
                          {result.rating && (
                            <Text style={styles.typeaheadItemRating}>
                              ⭐ {result.rating.toFixed(1)}
                            </Text>
                          )}
                        </View>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            )}

            {showTypeahead && searchResults.length === 0 && !searching && (
              <View style={styles.noResults}>
                <Text style={styles.noResultsText}>No providers found</Text>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Contact Method Selection Modal */}
      <Modal
        visible={contactMethodModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setContactMethodModalVisible(false)}
      >
        <TouchableWithoutFeedback onPress={() => setContactMethodModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <View style={styles.modalContent}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>Select Contact Method</Text>
                  <Pressable onPress={() => setContactMethodModalVisible(false)}>
                    <XCircle size={20} color="#6B7280" />
                  </Pressable>
                </View>
                
                {selectedProviderId && (() => {
                  const provider = providers.find(p => p.id === selectedProviderId);
                  if (!provider) return null;
                  
                  return (
                    <>
                      <View style={styles.modalProviderInfo}>
                        <Text style={styles.modalProviderName}>{provider.name}</Text>
                        <Text style={styles.modalProviderSpecialty}>{provider.specialty}</Text>
                      </View>

                      <ScrollView style={styles.methodOptions}>
                        {Object.entries(CONTACT_METHOD_CONFIG).map(([method, config]) => {
                          const MethodIcon = config.icon;
                          const isSelected = provider.contact_method === method;
                          const isSuggested = provider.suggested_contact_method === method;
                          const availability = getContactMethodAvailability(provider, method as ContactMethod);
                          const AvailabilityIcon = availability.icon;
                          
                          return (
                            <Pressable
                              key={method}
                              style={[
                                styles.methodOption,
                                isSelected && styles.methodOptionSelected,
                                !availability.available && styles.methodOptionDisabled,
                              ]}
                              onPress={() => availability.available && handleContactMethodChange(provider.id, method as ContactMethod)}
                              disabled={!availability.available}
                            >
                              <View style={[styles.methodOptionIcon, { backgroundColor: `${config.color}15` }]}>
                                <MethodIcon size={18} color={config.color} />
                              </View>
                              <View style={styles.methodOptionContent}>
                                <View style={styles.methodOptionHeader}>
                                  <Text style={[styles.methodOptionText, isSelected && styles.methodOptionTextSelected]}>
                                    {config.label}
                                  </Text>
                                  {isSuggested && (
                                    <View style={styles.suggestedBadge}>
                                      <Text style={styles.suggestedBadgeText}>AI Suggested</Text>
                                    </View>
                                  )}
                                </View>
                                <View style={styles.availabilityRow}>
                                  <AvailabilityIcon size={12} color={availability.color} />
                                  <Text style={[styles.availabilityText, { color: availability.color }]}>
                                    {availability.reason}
                                  </Text>
                                </View>
                              </View>
                              {isSelected && (
                                <View style={styles.methodOptionCheck}>
                                  <CheckCircle2 size={18} color={config.color} />
                                </View>
                              )}
                            </Pressable>
                          );
                        })}
                      </ScrollView>
                    </>
                  );
                })()}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  providersList: {
    gap: 12,
  },
  providerCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  providerNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#2563EB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  providerNumberText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  providerContent: {
    flex: 1,
    gap: 4,
  },
  providerName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  providerSpecialty: {
    fontSize: 13,
    color: '#6B7280',
  },
  providerReason: {
    fontSize: 12,
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
  providerActions: {
    gap: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  skipButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#DC2626',
  },
  restoreButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#16A34A',
  },
  skippedSection: {
    gap: 8,
    padding: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 6,
  },
  skippedTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6B7280',
    textTransform: 'uppercase',
  },
  skippedCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    opacity: 0.7,
  },
  skippedName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
    textDecorationLine: 'line-through',
  },
  skippedSpecialty: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  addSection: {
    gap: 12,
    padding: 16,
    backgroundColor: '#F0FDF4',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#BBF7D0',
  },
  addTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#166534',
  },
  searchContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#111827',
    padding: 0,
  },
  typeaheadDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    maxHeight: 300,
    zIndex: 1001,
    ...shadows.lg,
  },
  typeaheadScroll: {
    maxHeight: 300,
  },
  typeaheadItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  typeaheadItemDisabled: {
    opacity: 0.5,
  },
  typeaheadItemContent: {
    gap: 4,
  },
  typeaheadItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  typeaheadItemName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
  },
  addedBadge: {
    backgroundColor: '#DCFCE7',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  addedBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#16A34A',
  },
  typeaheadItemCategory: {
    fontSize: 13,
    color: '#6B7280',
  },
  typeaheadItemLocation: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  typeaheadItemLocationText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  typeaheadItemRating: {
    fontSize: 12,
    color: '#F59E0B',
    fontWeight: '500',
  },
  noResults: {
    padding: 16,
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    marginTop: 4,
  },
  noResultsText: {
    fontSize: 14,
    color: '#9CA3AF',
  },
  contactMethodContainer: {
    marginTop: 8,
    gap: 4,
  },
  contactMethodRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  contactMethodLabel: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  contactMethodBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
  },
  contactMethodText: {
    fontSize: 12,
    fontWeight: '500',
  },
  contactMethodOverride: {
    fontSize: 11,
    color: '#9CA3AF',
    fontStyle: 'italic',
    marginLeft: 80,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '100%',
    maxWidth: 480,
    maxHeight: '80%',
    ...shadows.xl,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  modalProviderInfo: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    gap: 4,
  },
  modalProviderName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
  },
  modalProviderSpecialty: {
    fontSize: 14,
    color: '#6B7280',
  },
  methodOptions: {
    padding: 16,
  },
  methodOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  methodOptionSelected: {
    backgroundColor: '#F0F9FF',
    borderColor: '#2563EB',
  },
  methodOptionDisabled: {
    opacity: 0.5,
  },
  methodOptionIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  methodOptionContent: {
    flex: 1,
    gap: 4,
  },
  methodOptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  methodOptionText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#111827',
  },
  methodOptionTextSelected: {
    color: '#2563EB',
    fontWeight: '600',
  },
  methodOptionCheck: {
    marginLeft: 'auto',
  },
  suggestedBadge: {
    backgroundColor: '#F3E8FF',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  suggestedBadgeText: {
    fontSize: 11,
    color: '#8B5CF6',
    fontWeight: '500',
  },
  availabilityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  availabilityText: {
    fontSize: 12,
    fontWeight: '500',
  },
});
